import api from './api';
import type {
  Task,
  TaskStatus,
  TaskBoardData,
  TaskStats,
  ApiResponse,
  TaskFormData,
} from '@/types';

interface TaskListResponse {
  data: Task[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    stats: TaskStats;
  };
}

interface TaskBoardResponse {
  data: TaskBoardData;
  meta: {
    stats: TaskStats;
  };
}

interface TaskFilters {
  status?: TaskStatus;
  priority?: string;
  assigned_to_me?: boolean;
  unassigned?: boolean;
  overdue?: boolean;
  due_today?: boolean;
  due_this_week?: boolean;
  sort?: 'due_date' | 'priority' | 'created_at' | 'position';
}

export const taskService = {
  // Get all tasks for a project
  async getTasks(
    projectId: string,
    filters: TaskFilters = {},
    page = 1,
    perPage = 50
  ): Promise<TaskListResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('per_page', String(perPage));

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await api.get<ApiResponse<TaskListResponse>>(
      `/projects/${projectId}/tasks?${params.toString()}`
    );
    return response.data.data;
  },

  // Get task board view (tasks grouped by status)
  async getTaskBoard(projectId: string): Promise<TaskBoardResponse> {
    const response = await api.get<ApiResponse<TaskBoardResponse>>(
      `/projects/${projectId}/tasks/board`
    );
    return response.data.data;
  },

  // Get a single task
  async getTask(projectId: string, taskId: string): Promise<Task> {
    const response = await api.get<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}`
    );
    return response.data.data;
  },

  // Create a new task
  async createTask(projectId: string, data: TaskFormData): Promise<Task> {
    const response = await api.post<ApiResponse<Task>>(
      `/projects/${projectId}/tasks`,
      { task: data }
    );
    return response.data.data;
  },

  // Update a task
  async updateTask(
    projectId: string,
    taskId: string,
    data: Partial<TaskFormData & { status?: TaskStatus; position?: number }>
  ): Promise<Task> {
    const response = await api.patch<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}`,
      { task: data }
    );
    return response.data.data;
  },

  // Delete a task
  async deleteTask(projectId: string, taskId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}`);
  },

  // Mark task as complete
  async completeTask(projectId: string, taskId: string): Promise<Task> {
    const response = await api.post<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}/complete`
    );
    return response.data.data;
  },

  // Reopen a completed task
  async reopenTask(projectId: string, taskId: string): Promise<Task> {
    const response = await api.post<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}/reopen`
    );
    return response.data.data;
  },

  // Assign task to a user
  async assignTask(
    projectId: string,
    taskId: string,
    assigneeId: string | null
  ): Promise<Task> {
    const response = await api.post<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}/assign`,
      { assignee_id: assigneeId }
    );
    return response.data.data;
  },

  // Reorder tasks (for drag and drop)
  async reorderTasks(
    projectId: string,
    tasks: Array<{ id: string; position: number; status?: TaskStatus }>
  ): Promise<void> {
    await api.post(`/projects/${projectId}/tasks/reorder`, { tasks });
  },

  // Update task status (convenience method)
  async updateTaskStatus(
    projectId: string,
    taskId: string,
    status: TaskStatus
  ): Promise<Task> {
    return this.updateTask(projectId, taskId, { status });
  },

  // Add label to task
  async addLabel(projectId: string, taskId: string, label: string): Promise<Task> {
    const task = await this.getTask(projectId, taskId);
    const labels = [...(task.labels || []), label];
    return this.updateTask(projectId, taskId, { labels } as never);
  },

  // Remove label from task
  async removeLabel(projectId: string, taskId: string, label: string): Promise<Task> {
    const task = await this.getTask(projectId, taskId);
    const labels = (task.labels || []).filter((l) => l !== label);
    return this.updateTask(projectId, taskId, { labels } as never);
  },
};

export default taskService;
